import { PacIntroCallAnswers } from '@bariendo/lib/patient-access-note';
import { GOAL_SHORT_LABELS } from '@bariendo/lib/patient-access-script';
import dayjs from 'dayjs';

/** Checkboxes that together make up the "Explain the procedure" beat. */
const PLAN_KEYS = ['pl_proc', 'pl_dur', 'pl_care', 'pl_q'];

function text(answers: PacIntroCallAnswers, id: string): string {
  const value = answers.values[id];
  return typeof value === 'string' ? value : '';
}

function checked(answers: PacIntroCallAnswers, id: string): boolean {
  return answers.values[id] === true;
}

/**
 * The one-line value a beat shows when collapsed on the legacy **scheduled**
 * intro call. Every row is one line closed: a title and its value. An empty
 * string means the beat is untouched, which is also what drives its tick.
 *
 * The inbound opening (`open2`) is gone from here along with the script that had
 * it — inbound calls run `summarizePacIntroCallBeat` now.
 */
export function summarizeBeat(beatId: string, answers: PacIntroCallAnswers): string {
  switch (beatId) {
    case 'open':
      return checked(answers, 'open_who') ? 'Confirmed' : '';

    case 'pathway':
      return text(answers, 'procedure');

    case 'whynow': {
      const goal = text(answers, 'wn_main');
      return goal ? (GOAL_SHORT_LABELS[goal] ?? goal) : '';
    }

    case 'plan': {
      const done = PLAN_KEYS.filter((key) => checked(answers, key)).length;
      if (done === PLAN_KEYS.length) return 'Covered';
      return done ? `${done} of ${PLAN_KEYS.length}` : '';
    }

    case 'loc':
      return text(answers, 'loc');

    case 'price':
      return text(answers, 'pay');

    case 'clinical':
      return checked(answers, 'c_consult') ? 'Offered' : '';

    case 'book': {
      if (!answers.slot) return '';
      const start = dayjs(answers.slot.start);
      return start.isValid() ? start.format('ddd · h:mma') : '';
    }

    case 'close':
      return text(answers, 'close');

    default:
      return '';
  }
}
